import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Select, Steps, Spin, message } from 'antd';
import { EnvironmentOutlined, ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import axios from '../../../utils/axiosConfig';
import './NewCampaign.css';

const { Option } = Select;
const { Step } = Steps;

const NewCampaign = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch locations (workspaces) and vehicle assignments from API
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch workspaces and assignments in parallel
      const [workspacesRes, assignmentsRes] = await Promise.all([
        axios.get('/api/vehicle-tracking/workspaces', { headers: authHeader() }),
        axios.get('/api/vehicle-tracking/assignments', { headers: authHeader() })
      ]);
      
      const workspaces = Array.isArray(workspacesRes.data) ? workspacesRes.data : (workspacesRes.data.items || []);
      const assignments = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : (assignmentsRes.data.items || []);
      setVehicleAssignments(assignments);
      
      // Transform to location format with vehicle count from assignments
      const transformedLocations = workspaces.map(ws => ({
        id: ws.id,
        name: ws.name,
        address: ws.address || 'Location',
        vehicles: assignments.filter(a => a.workspace_id === ws.id).length
      }));
      setLocations(transformedLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
      message.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleContinue = () => {
    if (selectedLocation) {
      // Navigate to campaign type selection with the selected location
      navigate('/agency/campaigns/new-campaign-ux', { state: { locationId: selectedLocation } });
    }
  };

  return (
    <div className="new-campaign-page">
      {/* Header */}
      <div className="new-campaign-header">
        <div>
          <h1 className="page-title">Create New Campaign</h1>
          <p className="page-subtitle">Select a location to start creating your campaign</p>
        </div>
      </div>

      {/* Progress Steps */}
      <Card className="steps-card">
        <Steps current={0} size="small">
          <Step title="Select Location" />
          <Step title="Campaign Type" />
          <Step title="Configure" />
          <Step title="Review & Launch" />
        </Steps>
      </Card>

      {/* Location Selection */}
      <Card className="location-selection-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>Select Location</h2>
            <p className="section-subtitle" style={{ margin: 0 }}>Choose which location this campaign will be associated with</p>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchLocations} loading={loading}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: '#666' }}>Loading locations...</p>
          </div>
        ) : locations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            <EnvironmentOutlined style={{ fontSize: 48, marginBottom: 16, color: '#ccc' }} />
            <p>No locations found. Please create a location first.</p>
            <Button type="primary" onClick={() => navigate('/agency/workspaces')}>
              Go to Locations
            </Button>
          </div>
        ) : (
          <div className="location-select-wrapper">
            <Select
              size="large"
              placeholder="Select a location..."
              value={selectedLocation}
              onChange={setSelectedLocation}
              style={{ width: '100%', maxWidth: 500 }}
              showSearch
              optionFilterProp="children"
            >
              {locations.map(location => (
                <Option key={location.id} value={location.id}>
                  <div className="location-option">
                    <EnvironmentOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                    <div>
                      <div className="location-option-name">{location.name}</div>
                      <div className="location-option-address">{location.address}</div>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        )}

        {selectedLocation && (
          <div className="selected-location-info">
            <Card className="location-info-card">
              <div className="location-info-header">
                <EnvironmentOutlined style={{ fontSize: 24, color: '#3b82f6' }} />
                <div>
                  <h3>{locations.find(l => l.id === selectedLocation)?.name}</h3>
                  <p>{locations.find(l => l.id === selectedLocation)?.address}</p>
                </div>
              </div>
              <div className="location-info-details">
                <div className="info-item">
                  <span className="info-label">Vehicles:</span>
                  <span className="info-value">{locations.find(l => l.id === selectedLocation)?.vehicles}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        <div className="location-actions">
          <Button size="large" onClick={() => navigate('/agency/campaigns')}>
            Cancel
          </Button>
          <Button 
            type="primary" 
            size="large" 
            icon={<ArrowRightOutlined />}
            onClick={handleContinue}
            disabled={!selectedLocation}
          >
            Continue to Campaign Type
          </Button>
        </div>
      </Card>

      {/* Info Card */}
      <Card className="info-card">
        <h3>Important Notes</h3>
        <ul>
          <li>Each location has its own Geotab account for vehicle tracking</li>
          <li>Campaign data and analytics will be specific to the selected location</li>
          <li>You can create multiple campaigns for the same location</li>
          <li>Team members with access to this location will be able to view and manage the campaign</li>
        </ul>
      </Card>
    </div>
  );
};

export default NewCampaign;
